import { useEffect, useMemo, useState } from 'react';
import { addDays, endOfDay, getMonth, getYear, startOfDay } from 'date-fns';
import { useSchool } from '@/contexts/SchoolContext';
import { useAuth } from '@/features/auth/hooks';
import taskService from '@/services/todo/TaskService';
import todoService from '@/services/todo/TodoService';
import useCalendarEvents from './useCalendarEvents';
import { eventsForDate, toCalendarDate } from '../utils/calendarDateUtils';

export default function useCalendarOverview() {
  const { schoolId, school } = useSchool();
  const { user } = useAuth();
  const today = useMemo(() => startOfDay(new Date()), []);
  const rangeEnd = useMemo(() => endOfDay(addDays(today, 7)), [today]);
  const { events, loading: eventsLoading, error: eventsError, refetch } = useCalendarEvents({
    month: getMonth(today),
    year: getYear(today),
    startDate: today,
    endDate: rangeEnd,
  });
  const [workItems, setWorkItems] = useState([]);
  const [workLoading, setWorkLoading] = useState(true);
  const [workError, setWorkError] = useState(null);

  useEffect(() => {
    let active = true;
    if (!schoolId || !user?.uid) {
      setWorkItems([]);
      setWorkLoading(false);
      return () => { active = false; };
    }

    setWorkLoading(true);
    setWorkError(null);
    Promise.allSettled([
      taskService.getTasksForUser(schoolId, user.uid),
      todoService.getTodosForUser(schoolId, user.uid),
    ]).then(([taskResult, todoResult]) => {
      if (!active) return;
      const tasks = taskResult.status === 'fulfilled' ? taskResult.value : [];
      const todos = todoResult.status === 'fulfilled' ? todoResult.value : [];
      const projected = [
        ...tasks
          .filter((task) => task.status !== 'completed' && task.dueDate)
          .map((task) => ({ ...task, kind: 'task', href: '/my-tasks' })),
        ...todos
          .filter((todo) => !todo.completed && todo.dueDate)
          .map((todo) => ({ ...todo, kind: 'todo', href: '/my-todos' })),
      ].filter((item) => {
        const due = toCalendarDate(item.dueDate);
        return due && due <= rangeEnd;
      }).sort((a, b) => (
        toCalendarDate(a.dueDate).getTime() - toCalendarDate(b.dueDate).getTime()
      ));

      setWorkItems(projected);
      if (taskResult.status === 'rejected' || todoResult.status === 'rejected') {
        setWorkError('Some work dates are unavailable.');
      }
      setWorkLoading(false);
    });

    return () => { active = false; };
  }, [rangeEnd, schoolId, user?.uid]);

  const todayEvents = useMemo(() => eventsForDate(events, today), [events, today]);
  const upcomingEvents = useMemo(() => events.filter((event) => {
    const start = toCalendarDate(event.startDate);
    return start && start > endOfDay(today) && start <= rangeEnd;
  }), [events, rangeEnd, today]);

  return {
    schoolName: school?.name || school?.schoolName || 'your school',
    today,
    rangeEnd,
    events,
    todayEvents,
    upcomingEvents,
    workItems,
    eventsLoading,
    eventsError,
    workLoading,
    workError,
    refetch,
  };
}
